// TD-1976 Phase 6 — Settings → Security → MFA tab.
//
// Renders three sub-views inside the existing Profile tab shell:
//   - 'status'  → status card (Enable/Disable/Setup pending), method, backup
//   - 'enable'  → inline MfaSetupForm in optional mode (no amber blocker)
//   - 'disable' → re-auth panel (OtpInput → DELETE /mfa/disable)
//
// The Profile shell expects the hook to expose isActionsHidden:true because
// this tab handles its own primary CTAs (Enable / Disable / Cancel) inline.

import { observer, useLocalObservable } from 'mobx-react';
import { runInAction } from 'mobx';
import { Fragment, useEffect } from 'react';
import { Link } from 'react-router';
import { useStore } from '../../../../store/store';
import { paths } from '../../../../utils/constants/routes';
import { MFA_OTP_REGEX } from '../../../../utils/constants/fields';
import MfaSetupForm from '../../MfaSetup/components/MfaSetupForm';
import MfaLockCountdown from '../../MfaSetup/components/MfaLockCountdown';
import OtpInput from '../../../../components/inputs/OtpInput';
import { isRotationReauthDone } from './rotationGate';
import '../../MfaSetup/components/MfaSetupForm.less';
import './TabSecurity.less';

const OTP_LENGTH = 6;

const StatusView = observer(({ onEnable, onDisable, onRotate }) => {
  const { authStore } = useStore();
  const status = authStore.mfaStatus;

  if (authStore.isLoadingMfaStatus && !status) {
    return <p className="lede">Loading MFA status…</p>;
  }
  if (authStore.mfaStatusError && !status) {
    return (
      <div className="error">
        {authStore.mfaStatusError}{' '}
        <button
          type="button"
          className="link"
          onClick={() => authStore.loadMfaStatus()}>
          Try again
        </button>
      </div>
    );
  }
  if (!status) {
    return null;
  }

  const { enabled, required } = status;
  // Backend-derived: required can only be true when enabled is false (means
  // user is in the mfa-required group but hasn't set up yet). The defensive
  // `enabled && required` branch handles a hypothetical backend change.
  const setupPending = !enabled && required;
  const enabledRequired = enabled && required; // unreachable per backend contract

  let pillClass = 'pill-neutral';
  let pillLabel = 'Not enabled';
  if (enabled) {
    pillClass = 'pill-ok';
    pillLabel = 'Enabled';
  } else if (setupPending) {
    pillClass = 'pill-warn';
    pillLabel = 'Setup pending';
  }

  return (
    <div className="security-card">
      <div className="security-row">
        <div className="meta">
          <h3>
            Two-factor authentication (MFA){' '}
            <span className={`pill ${pillClass}`}>{pillLabel}</span>
          </h3>
          <p>
            Add an extra step when signing in. We&rsquo;ll ask for a 6-digit code from
            your authenticator app on top of your password.
          </p>
          {setupPending && (
            <div className="alert alert-warn mt-2">
              Your workspace requires MFA. Setup is not complete yet — finish
              the QR step before continuing.
            </div>
          )}
          {enabledRequired && (
            <div className="alert alert-info mt-2">
              Your workspace requires MFA for all members. You can&rsquo;t disable
              it from here — contact your admin if this needs to change.
            </div>
          )}
        </div>
        <div className="ctl">
          {!enabled && !setupPending && (
            <button
              type="button"
              className="btn btn-primary btn-short"
              onClick={onEnable}>
              Enable MFA
            </button>
          )}
          {setupPending && (
            <Link to={paths.MFA_SETUP} className="btn btn-primary btn-short">
              Continue setup
            </Link>
          )}
          {enabled && !required && (
            <>
              {/* Rotation: the current authenticator keeps working until the
                  new one is verified. Surfaced as a secondary action; the
                  RotateView panel explains this before triggering. */}
              <button
                type="button"
                className="btn btn-transparent btn-short"
                onClick={onRotate}>
                Generate new setup key
              </button>
              <button
                type="button"
                className="btn btn-transparent btn-short security-danger"
                onClick={onDisable}>
                Disable MFA
              </button>
            </>
          )}
        </div>
      </div>

      <div className="security-row">
        <div className="meta">
          <h3>Authentication method</h3>
          <p>
            Authenticator app (TOTP). Supported: Google Authenticator, Microsoft
            Authenticator, 1Password, Authy. SMS and email are not supported by
            design.
          </p>
        </div>
        <div className="ctl">
          <span className="pill pill-neutral">Authenticator app</span>
        </div>
      </div>

      <div className="security-row">
        <div className="meta">
          <h3>Backup &amp; recovery</h3>
          <p>
            Recovery codes are not available yet. If you lose access to your
            authenticator, contact your workspace admin.
          </p>
        </div>
        <div className="ctl">
          <span className="pill pill-warn">Coming soon</span>
        </div>
      </div>
    </div>
  );
});

const EnableView = observer(({ onDone, onCancel }) => {
  return (
    <div className="security-inline-panel">
      <div className="security-inline-header">
        <h3>Enable MFA</h3>
        <button type="button" className="btn btn-transparent btn-short" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <MfaSetupForm enforced={false} onComplete={onDone} />
    </div>
  );
});

const DisableView = observer(({ onDone, onCancel }) => {
  const { authStore } = useStore();
  const state = useLocalObservable(() => ({
    code: '',
    submitted: false,
    setCode(value) {
      runInAction(() => {
        state.code = value;
      });
    }
  }));

  // Watch for status transition disabled→true to mean disable succeeded.
  // (We can't use a useEffect on isDisablingMfa false alone — that fires for
  // failures too.) When status flips to enabled:false AND the user submitted
  // from this panel, the action chain completed.
  useEffect(() => {
    if (
      state.submitted &&
      !authStore.isDisablingMfa &&
      authStore.mfaStatus?.enabled === false
    ) {
      onDone?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStore.isDisablingMfa, authStore.mfaStatus?.enabled]);

  // TD-2192: a backend lock (from any MFA code flow) blocks entry and keeps
  // its text up after Cancel + reopen cleared mfaDisableError.
  const isLocked = authStore.isMfaCodeLocked;
  const errorText = authStore.mfaCodeLockMessage || authStore.mfaDisableError;

  const onSubmit = (e) => {
    e.preventDefault();
    if (!MFA_OTP_REGEX.test(state.code) || authStore.isDisablingMfa || isLocked) return;
    runInAction(() => {
      state.submitted = true;
    });
    authStore.disableMfa(state.code);
  };

  return (
    <div className="security-inline-panel">
      <div className="security-inline-header">
        <h3>Disable MFA</h3>
        <button type="button" className="btn btn-transparent btn-short" onClick={onCancel}>
          Cancel
        </button>
      </div>

      <div className="alert alert-warn mb-3">
        <b>Heads up:</b> after disabling, you&rsquo;ll only need your password to
        sign in. <b>If this account is shared, this disables MFA for everyone
        using it.</b>
      </div>

      <form onSubmit={onSubmit}>
        <div className="form-group">
          <label htmlFor="mfa-disable-0">Authentication code</label>
          <p className="small">
            Enter the current 6-digit code from your authenticator app to confirm.
          </p>
          <OtpInput
            length={OTP_LENGTH}
            value={state.code}
            onChange={state.setCode}
            onComplete={state.setCode}
            error={!!errorText}
            disabled={authStore.isDisablingMfa || isLocked}
            autoFocus
            name="mfa-disable"
          />
        </div>

        {errorText && <div className="error">{errorText}</div>}
        <MfaLockCountdown />

        <button
          type="submit"
          className="btn btn-primary"
          disabled={!MFA_OTP_REGEX.test(state.code) || authStore.isDisablingMfa || isLocked}>
          {authStore.isDisablingMfa ? 'Disabling…' : 'Disable MFA'}
        </button>
      </form>
    </div>
  );
});

/**
 * RotateView — re-auth with the current TOTP code, then drop straight into
 * the standard MfaSetupForm (which now sees the secret already loaded and
 * skips its own startMfaSetup call). The success state in MfaSetupForm
 * detects `mfaSetupMode === 'rotate'` and swaps the copy.
 *
 * Cognito replaces the current software token only once the new one is
 * verified, so the previous authenticator keeps working until verify
 * succeeds. Cancelling mid-rotation is safe (tested on dev, TD-2983).
 */
const RotateView = observer(({ onDone, onCancel }) => {
  const { authStore } = useStore();

  const state = useLocalObservable(() => ({
    code: '',
    submitted: false,
    setCode(value) {
      runInAction(() => {
        state.code = value;
        // Typing after an error hides the error so the user can retype the
        // code without re-rendering the failed state.
        state.submitted = false;
      });
    }
  }));

  const reauthInFlight = authStore.isAuthenticating && !authStore.mfaSetupSecret;
  // TD-2192: the backend lock text stays up while typing (which hides the
  // ordinary re-auth error) and blocks entry until the lock lifts.
  const isLocked = authStore.isMfaCodeLocked;
  const reauthErrorText =
    authStore.mfaCodeLockMessage || (state.submitted ? authStore.mfaSetupError : null);
  const reauthSucceeded = isRotationReauthDone(authStore);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!MFA_OTP_REGEX.test(state.code) || reauthInFlight || isLocked) return;
    runInAction(() => {
      state.submitted = true;
    });
    authStore.startMfaSetup(state.code, 'rotate');
  };

  if (reauthSucceeded) {
    return (
      <div className="security-inline-panel">
        <div className="security-inline-header">
          <h3>Generate new setup key</h3>
          <button type="button" className="btn btn-transparent btn-short" onClick={onCancel}>
            Cancel
          </button>
        </div>
        <MfaSetupForm enforced={false} onComplete={onDone} />
      </div>
    );
  }

  return (
    <div className="security-inline-panel">
      <div className="security-inline-header">
        <h3>Generate new setup key</h3>
        <button type="button" className="btn btn-transparent btn-short" onClick={onCancel}>
          Cancel
        </button>
      </div>

      <div className="alert alert-warn mb-3">
        <b>Heads up:</b> your current authenticator keeps working until you
        confirm the new one. Scan the new QR code and enter a fresh code to
        finish; if you cancel before that, nothing changes. <b>If this account
        is shared, everyone using it must re-scan the new key.</b>
      </div>

      <form onSubmit={onSubmit}>
        <div className="form-group">
          <label htmlFor="mfa-rotate-0">Current authentication code</label>
          <p className="small">
            Enter a 6-digit code from your <b>current</b> authenticator to
            confirm rotation.
          </p>
          <OtpInput
            length={OTP_LENGTH}
            value={state.code}
            onChange={state.setCode}
            onComplete={state.setCode}
            error={!!reauthErrorText}
            disabled={reauthInFlight || isLocked}
            autoFocus
            name="mfa-rotate"
          />
        </div>

        {reauthErrorText && <div className="error">{reauthErrorText}</div>}
        <MfaLockCountdown />

        <button
          type="submit"
          className="btn btn-primary"
          disabled={!MFA_OTP_REGEX.test(state.code) || reauthInFlight || isLocked}>
          {reauthInFlight ? 'Verifying…' : 'Generate new setup key'}
        </button>
      </form>
    </div>
  );
});

const useSecurityTab = () => {
  const { authStore } = useStore();

  const state = useLocalObservable(() => ({
    view: 'status', // 'status' | 'enable' | 'disable' | 'rotate'
    setView(next) {
      runInAction(() => {
        state.view = next;
      });
    }
  }));

  useEffect(() => {
    authStore.loadMfaStatus();
    return () => {
      authStore.clearMfaDisableError();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goStatus = () => {
    authStore.loadMfaStatus();
    // Drop any in-memory secret payload from the rotation flow before
    // returning to status. The MfaSetupForm unmount-effect already calls
    // clearMfaSetup, but doing it here too keeps the cleanup centralised
    // for paths that don't go through MfaSetupForm (cancel from re-auth).
    authStore.clearMfaSetup();
    state.setView('status');
  };

  const view = (
    <Fragment key="tab-security">
      <div className="mobile-tab-header">
        <Link to={paths.USER_PROFILE_SETTINGS} className="backlink">
          Back to settings
        </Link>
        <div className="title">Security</div>
        <p>Multi-factor authentication and account security.</p>
      </div>

      <div className="form-body form-row-layout">
        {state.view === 'status' && (
          <StatusView
            onEnable={() => state.setView('enable')}
            onDisable={() => {
              authStore.clearMfaDisableError();
              state.setView('disable');
            }}
            onRotate={() => {
              // Pre-clear any stale setup state before entering rotation so
              // the re-auth gate starts from a clean slate.
              authStore.clearMfaSetup();
              state.setView('rotate');
            }}
          />
        )}
        {state.view === 'enable' && (
          <EnableView onDone={goStatus} onCancel={() => state.setView('status')} />
        )}
        {state.view === 'disable' && (
          <DisableView onDone={goStatus} onCancel={() => state.setView('status')} />
        )}
        {state.view === 'rotate' && (
          <RotateView onDone={goStatus} onCancel={goStatus} />
        )}
      </div>
    </Fragment>
  );

  // Save/Restore buttons in the page header are irrelevant here — this tab
  // handles its own inline CTAs.
  return { view, isActionsHidden: true };
};

export default useSecurityTab;
